import { Link } from "react-router-dom";
import SEOMeta from "@/components/SEOMeta";
import { usePublicBlogPosts } from "@/hooks/useBlogPosts";
import NavbarNew from "@/components/NavbarNew";
import FooterNew from "@/components/FooterNew";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { readingTimeLabel } from "@/lib/readingTime";
import { LoadErrorState } from "@/components/shared/LoadErrorState";
import { ERP_PILLAR_ENABLED } from "@/data/automacaoServices";
import { Photo } from "@/components/shared/Photo";

const Blog = () => {
  const { data: posts = [], isLoading: loading, isError, refetch } = usePublicBlogPosts();

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "";
    return format(new Date(dateString), "dd/MM/yyyy", { locale: ptBR });
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOMeta
        title="Blog | Eliveuton Dev"
        description={`O que aprendi implantando automação em empresa de verdade. Artigos sobre CRM, atendimento${ERP_PILLAR_ENABLED ? ", ERP" : ""} e automação com IA.`}
        canonicalUrl="/blog"
      />
      <NavbarNew />

      <main>
        <div className="mx-auto max-w-[860px] px-8 pb-[90px] pt-[76px] max-[900px]:px-5">
          <h1 className="text-[44px] font-bold leading-[1.1] tracking-[-0.04em] max-[900px]:text-[38px]">
            Blog
          </h1>
          <p className="mt-[18px] text-[16.5px] text-muted-foreground">
            O que aprendi implantando automação em empresa de verdade.
          </p>

          <div className="mt-10 flex flex-col">
            {loading ? (
              [1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="flex animate-pulse items-center gap-[22px] border-b border-border py-[22px]"
                >
                  <div className="flex-1 space-y-3">
                    <div className="h-3 w-1/3 rounded bg-muted" />
                    <div className="h-5 w-3/4 rounded bg-muted" />
                    <div className="h-4 w-full rounded bg-muted" />
                  </div>
                  <div className="h-[100px] w-[150px] shrink-0 rounded-xl bg-muted" />
                </div>
              ))
            ) : isError ? (
              <LoadErrorState
                size="sm"
                title="Não foi possível carregar os artigos."
                onRetry={() => refetch()}
              />
            ) : posts.length === 0 ? (
              <p className="py-[22px] text-[14px] text-muted-foreground">
                Nenhum artigo publicado ainda. Volte em breve!
              </p>
            ) : (
              posts.map((post) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className="-mx-3 flex items-center gap-[22px] rounded-lg border-b border-border px-3 py-[22px] transition-colors hover:bg-muted"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2.5 text-[11.5px] text-muted-foreground">
                      {post.blog_categories && (
                        <>
                          <span className="font-bold text-primary">
                            {post.blog_categories.name}
                          </span>
                          <span>·</span>
                        </>
                      )}
                      <span>{formatDate(post.published_at || post.created_at)}</span>
                      <span>·</span>
                      <span>{readingTimeLabel(post.content)}</span>
                    </div>
                    <h3 className="mt-[9px] text-[19px] font-bold leading-[1.35] tracking-[-0.02em]">
                      {post.title}
                    </h3>
                    {post.excerpt && (
                      <p className="mt-2 text-[14px] leading-[1.6] text-muted-foreground">
                        {post.excerpt}
                      </p>
                    )}
                  </div>
                  <div className="h-[100px] w-[150px] shrink-0 overflow-hidden rounded-xl border border-border bg-card max-[600px]:hidden">
                    {post.cover_image_url ? (
                      <img
                        src={post.cover_image_url}
                        alt={post.title}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <Photo id="mesa-trabalho" decorative sizes="150px" />
                    )}
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
      </main>

      <FooterNew />
    </div>
  );
};

export default Blog;
